import React from 'react';

import { CardContent } from '@biome-design-system/ui/CardContent';
import { CardMedia } from '@biome-design-system/ui/CardMedia';
import { CardHeader } from '@biome-design-system/ui/CardHeader';
import { CardActions } from '@biome-design-system/ui/CardActions';
import { Typography } from '@biome-design-system/ui/Typography';
import { Button } from '@biome-design-system/ui/Button';
import { HeartInHandIcon } from '@biome-design-system/icons';
import { type Log } from 'Src/types';

import { FormattedMessage } from '@kyruus/intl';

import { messages } from './messages';
import { ValueAddProgramsContainer } from '../styles';
import { StyledMessageCard } from 'Src/search-v9/ValueAddPrograms/styles';

interface ValueAddProgramsProps {
  valueAddPrograms: ValueAddProgram[];
  log: Log;
}

export interface ValueAddProgram {
  resource_name: string;
  resource_type: string;
  resource_description: string;
  link_to_access_resource?: string | null;
  link_to_enrollment_resource?: string | null;
  program_logo_link?: string | null;
}

export const ValueAddPrograms: React.FC<ValueAddProgramsProps> = ({
  valueAddPrograms,
  log
}) => {
  if (!valueAddPrograms || valueAddPrograms.length === 0) {
    return null;
  }

  const {
    resource_name,
    resource_type,
    resource_description,
    program_logo_link,
    link_to_enrollment_resource,
    link_to_access_resource
  } = valueAddPrograms[0];

  return (
    <ValueAddProgramsContainer>
      {/* overriding default biome message card styles to fit our needs for this component */}
      <StyledMessageCard data-testid="vap-message-card" variant="outlined">
        {program_logo_link && (
          <CardMedia
            data-testid="vap-card-media"
            alt={`${resource_name} Logo`}
            image={program_logo_link}
            component="img"
          />
        )}
        {!program_logo_link && (
          <HeartInHandIcon
            data-testid="heart-hand-icon"
            fontSize="inherit"
            sx={{ gridArea: 'CardMedia', height: '128px', width: '128px' }}
          />
        )}
        <CardHeader
          data-testid="vap-card-header"
          title={resource_name}
          subheader={resource_type}
        />
        <CardContent data-testid="vap-card-content" sx={{}}>
          <Typography color="text.secondary" variant="body2">
            {resource_description}
          </Typography>
        </CardContent>
        <CardActions data-testid="vap-card-actions">
          {link_to_enrollment_resource && (
            <Button
              variant="outlined"
              href={link_to_enrollment_resource}
              target="_blank"
              rel="noopener noreferrer"
              component="a"
              onClick={() =>
                log('user_action.value_add_programs_link.enroll.clicked', {
                  link_to_enrollment_resource
                })
              }
            >
              <FormattedMessage {...messages.enrollNowButtonText} />
            </Button>
          )}
          {link_to_access_resource && (
            <Button
              variant="outlined"
              color="neutral"
              target="_blank"
              rel="noopener noreferrer"
              href={link_to_access_resource}
              onClick={() =>
                log('user_action.value_add_programs_link.learn_more.clicked', {
                  link_to_access_resource
                })
              }
              component="a"
            >
              <FormattedMessage {...messages.learnMoreButtonText} />
            </Button>
          )}
        </CardActions>
      </StyledMessageCard>
    </ValueAddProgramsContainer>
  );
};
